import { useEffect, useState } from 'react';
import {
  Copy, Trash, FlipHorizontal, FlipVertical, ArrowCounterClockwise, CornersOut,
  AlignLeft, AlignCenterHorizontal, AlignRight, AlignTop, AlignCenterVertical, AlignBottom,
  CaretDown,
} from '@phosphor-icons/react';
import { useSceneStore } from '../../stores/sceneStore';
import { getSpineManager } from '../../lib/spine-manager';
import { api } from '../../lib/api-client';
import TransformEditor from './TransformEditor';
import AnimationTracks from './AnimationTracks';
import SkinSelector from './SkinSelector';
import BoneAttachment from './BoneAttachment';
import IKConstraintEditor from './IKConstraintEditor';
import PhysicsConstraintEditor from './PhysicsConstraintEditor';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Tooltip } from '../ui/tooltip';
import type { Asset } from '@spine-workspace/shared';

// ── Collapsible section ────────────────────────────────────────────────────────

interface CollapsibleSectionProps {
  label: string;
  defaultOpen?: boolean;
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
}

function CollapsibleSection({ label, defaultOpen = true, headerExtra, children }: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border-subtle">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-bg-hover transition-colors"
      >
        <CaretDown
          size={10}
          className={`text-text-tertiary transition-transform shrink-0 ${open ? '' : '-rotate-90'}`}
        />
        <span className="font-serif text-sm font-medium text-text-secondary tracking-[0.01em] flex-1 text-left">
          {label}
        </span>
        {headerExtra}
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}

// ── Tab bar ────────────────────────────────────────────────────────────────────

type Tab = 'inspect' | 'ik' | 'physics';
const TABS: { id: Tab; label: string }[] = [
  { id: 'inspect', label: 'Inspect' },
  { id: 'ik', label: 'IK' },
  { id: 'physics', label: 'Physics' },
];

// ── Main component ─────────────────────────────────────────────────────────────

export default function LayerProperties() {
  const { selectedLayer, layers, removeLayer } = useSceneStore();
  const layer = selectedLayer();
  const [asset, setAsset] = useState<Asset | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('inspect');

  useEffect(() => {
    if (!layer) { setAsset(null); return; }
    api.get<{ asset: Asset }>(`/api/assets/${layer.assetId}`)
      .then(({ asset }) => setAsset(asset))
      .catch(() => setAsset(null));
  }, [layer?.assetId]);

  if (!layer) return null;

  const isImageLayer = layer.layerType === 'image';
  const animations = asset?.metadata?.animations ?? [];
  const skins = asset?.metadata?.skins ?? ['default'];
  const isFlippedH = layer.transform.scaleX < 0;
  const isFlippedV = layer.transform.scaleY < 0;

  function flipH() {
    useSceneStore.getState().updateLayerTransform(layer!.id, { scaleX: -layer!.transform.scaleX });
  }
  function flipV() {
    useSceneStore.getState().updateLayerTransform(layer!.id, { scaleY: -layer!.transform.scaleY });
  }

  type AlignDir = 'left' | 'centerH' | 'right' | 'top' | 'centerV' | 'bottom';
  function alignLayer(dir: AlignDir) {
    const store = useSceneStore.getState();
    const { canvasWidth, canvasHeight } = store;
    const hw = canvasWidth / 2;
    const hh = canvasHeight / 2;
    const updates: Record<AlignDir, Partial<{ x: number; y: number }>> = {
      left: { x: -hw }, centerH: { x: 0 }, right: { x: hw },
      top: { y: -hh }, centerV: { y: 0 }, bottom: { y: hh },
    };
    store.pushUndo();
    store.updateLayerTransform(layer!.id, updates[dir]);
  }

  const transformReset = (
    <button
      title="Reset to default transform"
      onClick={(e) => {
        e.stopPropagation();
        const store = useSceneStore.getState();
        store.pushUndo();
        store.updateLayerTransform(layer.id, { x: 0, y: 0, scaleX: 1, scaleY: 1, rotation: 0 });
      }}
      className="flex items-center gap-1 text-text-tertiary hover:text-text-primary hover:bg-bg-hover rounded px-1.5 py-0.5 text-xs font-mono transition-all duration-fast"
    >
      <ArrowCounterClockwise size={11} />
      Reset
    </button>
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Asset name row */}
      <div className="px-4 py-3 border-b border-border-subtle flex items-center gap-2 shrink-0">
        <input
          title="Layer name"
          placeholder="Layer name"
          className="flex-1 min-w-0 bg-bg-input border border-border text-text-primary rounded-md px-3 py-[7px] text-sm font-sans
                     hover:bg-bg-hover focus:outline-none focus:border-accent focus:shadow-focus transition-all duration-normal"
          value={layer.name}
          onChange={(e) => useSceneStore.getState().renameLayer(layer.id, e.target.value)}
        />
        <Tooltip content="Focus layer in viewport">
          <button
            data-testid="focus-layer-btn"
            onClick={() => getSpineManager().focusLayer(layer.id)}
            className="w-[34px] h-[34px] flex items-center justify-center rounded-md border border-border bg-bg-input text-text-secondary hover:text-accent hover:border-accent hover:bg-accent-muted transition-all duration-fast shrink-0"
          >
            <CornersOut size={14} />
          </button>
        </Tooltip>
      </div>

      {/* Tab bar — spine layers only */}
      {!isImageLayer && (
        <div className="flex shrink-0 border-b border-border-subtle">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex-1 py-2 text-xs font-sans font-medium transition-colors ${
                activeTab === id
                  ? 'text-accent border-b-2 border-accent -mb-px'
                  : 'text-text-tertiary hover:text-text-secondary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">

        {/* ── Inspect tab ─────────────────────────────────────────────────── */}
        {(isImageLayer || activeTab === 'inspect') && (
          <>
            {/* Align */}
            <CollapsibleSection label="Align" defaultOpen>
              <div className="flex items-center justify-between mb-2">
                <span className="text-text-tertiary text-[10px] font-sans">to canvas</span>
              </div>
              <div className="grid grid-cols-3 gap-1">
                {([
                  { dir: 'left' as AlignDir, Icon: AlignLeft, tip: 'Align left edge to canvas left' },
                  { dir: 'centerH' as AlignDir, Icon: AlignCenterHorizontal, tip: 'Center horizontally on canvas' },
                  { dir: 'right' as AlignDir, Icon: AlignRight, tip: 'Align right edge to canvas right' },
                  { dir: 'top' as AlignDir, Icon: AlignTop, tip: 'Align top edge to canvas top' },
                  { dir: 'centerV' as AlignDir, Icon: AlignCenterVertical, tip: 'Center vertically on canvas' },
                  { dir: 'bottom' as AlignDir, Icon: AlignBottom, tip: 'Align bottom edge to canvas bottom' },
                ] as const).map(({ dir, Icon, tip }) => (
                  <Tooltip key={dir} content={tip}>
                    <button
                      onClick={() => alignLayer(dir)}
                      className="flex items-center justify-center h-[28px] rounded-md text-text-tertiary hover:text-accent hover:bg-accent-muted border border-border hover:border-accent/40 transition-all duration-fast"
                    >
                      <Icon size={13} />
                    </button>
                  </Tooltip>
                ))}
              </div>
            </CollapsibleSection>

            {/* Transform */}
            <CollapsibleSection label="Transform" defaultOpen headerExtra={transformReset}>
              <TransformEditor layer={layer} />
            </CollapsibleSection>

            {/* Appearance */}
            <CollapsibleSection label="Appearance" defaultOpen>
              {/* Opacity */}
              <div className="flex items-center gap-3 mb-2">
                <span className="font-sans text-xs font-medium text-text-secondary w-[64px] shrink-0">Opacity</span>
                <Slider
                  min={0} max={100} step={1}
                  value={[Math.round(layer.opacity * 100)]}
                  onValueChange={([v]) => useSceneStore.getState().setLayerOpacity(layer.id, v / 100)}
                  className="flex-1"
                />
                <span className="font-mono text-xs text-text-tertiary w-[32px] text-right shrink-0">
                  {Math.round(layer.opacity * 100)}%
                </span>
              </div>

              {/* Blend Mode */}
              <div className="flex items-center gap-3 mb-2">
                <span className="font-sans text-xs font-medium text-text-secondary w-[64px] shrink-0">Blend</span>
                <select
                  data-testid="blend-mode-select"
                  value={layer.blendMode ?? 'normal'}
                  onChange={(e) => useSceneStore.getState().setLayerBlendMode(layer.id, e.target.value as import('@spine-workspace/shared').LayerBlendMode)}
                  className="flex-1 bg-bg-input border border-border text-text-primary rounded-md px-2 py-[5px] text-xs font-sans
                             hover:border-border-focus focus:outline-none focus:border-accent transition-all duration-fast cursor-pointer"
                >
                  <option value="normal">Normal</option>
                  <optgroup label="Lighten">
                    <option value="add">Add</option>
                    <option value="screen">Screen</option>
                    <option value="color-dodge">Color Dodge</option>
                  </optgroup>
                  <optgroup label="Darken">
                    <option value="multiply">Multiply</option>
                    <option value="color-burn">Color Burn</option>
                  </optgroup>
                  <optgroup label="Contrast">
                    <option value="overlay">Overlay</option>
                    <option value="hard-light">Hard Light</option>
                    <option value="soft-light">Soft Light</option>
                  </optgroup>
                  <optgroup label="Difference">
                    <option value="difference">Difference</option>
                    <option value="exclusion">Exclusion</option>
                  </optgroup>
                </select>
              </div>

              {/* Color Effects */}
              <div className="space-y-2 mb-2">
                <span className="font-sans text-xs font-medium text-text-secondary block">Color Effects</span>
                <div className="flex items-center gap-3">
                  <span className="font-sans text-xs text-text-tertiary w-[64px] shrink-0">Brightness</span>
                  <Slider
                    min={0} max={2} step={0.01}
                    value={[layer.effects?.brightness ?? 1]}
                    onValueChange={([v]) => useSceneStore.getState().setLayerEffects(layer.id, { brightness: v })}
                    className="flex-1"
                  />
                  <span className="font-mono text-xs text-text-tertiary w-[32px] text-right shrink-0">
                    {(layer.effects?.brightness ?? 1).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-sans text-xs text-text-tertiary w-[64px] shrink-0">Saturation</span>
                  <Slider
                    min={-1} max={1} step={0.01}
                    value={[layer.effects?.saturation ?? 0]}
                    onValueChange={([v]) => useSceneStore.getState().setLayerEffects(layer.id, { saturation: v })}
                    className="flex-1"
                  />
                  <span className="font-mono text-xs text-text-tertiary w-[32px] text-right shrink-0">
                    {(layer.effects?.saturation ?? 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-sans text-xs text-text-tertiary w-[64px] shrink-0">Tint</span>
                  <input
                    type="color"
                    value={layer.effects?.tint ?? '#ffffff'}
                    onChange={(e) => useSceneStore.getState().setLayerEffects(layer.id, { tint: e.target.value })}
                    className="w-8 h-7 rounded cursor-pointer border border-border bg-bg-input"
                  />
                  <button
                    onClick={() => useSceneStore.getState().setLayerEffects(layer.id, { tint: null })}
                    className="text-xs text-text-tertiary hover:text-text-primary font-sans px-2 py-1 rounded hover:bg-bg-hover transition-all duration-fast"
                    title="Remove tint"
                  >
                    None
                  </button>
                  {layer.effects?.tint && (
                    <span className="font-mono text-xs text-text-tertiary">{layer.effects.tint}</span>
                  )}
                </div>
              </div>

              {/* Flip */}
              <div className="flex items-center gap-2">
                <span className="font-sans text-xs font-medium text-text-secondary w-[64px] shrink-0">Flip</span>
                <button
                  title="Flip Horizontal"
                  onClick={flipH}
                  className={`flex items-center gap-1.5 px-3 py-[5px] rounded-md text-xs font-sans transition-all duration-fast ${
                    isFlippedH ? 'bg-accent-muted text-accent outline outline-[1.5px] outline-accent/30' : 'bg-bg-input border border-border text-text-secondary hover:bg-bg-hover'
                  }`}
                >
                  <FlipHorizontal size={13} /> H
                </button>
                <button
                  title="Flip Vertical"
                  onClick={flipV}
                  className={`flex items-center gap-1.5 px-3 py-[5px] rounded-md text-xs font-sans transition-all duration-fast ${
                    isFlippedV ? 'bg-accent-muted text-accent outline outline-[1.5px] outline-accent/30' : 'bg-bg-input border border-border text-text-secondary hover:bg-bg-hover'
                  }`}
                >
                  <FlipVertical size={13} /> V
                </button>
              </div>
            </CollapsibleSection>

            {/* Animation — spine only */}
            {!isImageLayer && animations.length > 0 && (
              <CollapsibleSection label="Animation" defaultOpen>
                <AnimationTracks layer={layer} animations={animations} />
              </CollapsibleSection>
            )}

            {/* Skin — spine only */}
            {!isImageLayer && skins.length > 0 && (
              <CollapsibleSection label="Skin" defaultOpen>
                <SkinSelector layer={layer} skins={skins} />
              </CollapsibleSection>
            )}

            {/* Attachment — spine only */}
            {!isImageLayer && (
              <CollapsibleSection label="Attachment" defaultOpen={false}>
                <BoneAttachment layer={layer} allLayers={layers} />
              </CollapsibleSection>
            )}

            {/* Actions */}
            <div className="flex gap-2 px-4 py-3">
              <Button
                variant="violet"
                size="sm"
                className="flex-1 gap-1.5"
                onClick={() => useSceneStore.getState().duplicateLayer(layer.id)}
              >
                <Copy size={14} />
                Duplicate
              </Button>
              <Button
                variant="danger"
                size="sm"
                className="flex-1 gap-1.5"
                onClick={() => removeLayer(layer.id)}
              >
                <Trash size={14} />
                Remove
              </Button>
            </div>
          </>
        )}

        {/* ── IK tab ──────────────────────────────────────────────────────── */}
        {!isImageLayer && activeTab === 'ik' && (
          <div className="px-4 py-3">
            <IKConstraintEditor layer={layer} />
          </div>
        )}

        {/* ── Physics tab ─────────────────────────────────────────────────── */}
        {!isImageLayer && activeTab === 'physics' && (
          <div className="px-4 py-3">
            <PhysicsConstraintEditor layer={layer} />
          </div>
        )}

      </div>
    </div>
  );
}
